import React from "react";
import { Box, Tooltip } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

/**
 * "The trainee is not doing the exercise you programmed."
 *
 * <p>Mirrors the swap marker in the nutrition log (`nutrition/v2/foodlog/LogFoodRow`), which shows
 * `במקום <planned food>` behind the same icon and warning colour, so a coach reads a substitution
 * the same way on both sides of the app.
 *
 * <p>Renders nothing unless the server derived a swap: `replacedByTrainee` is computed from
 * `exercise_id` vs `original_exercise_id` on the server (family-aware, so a company variant of the
 * prescribed exercise is not a swap) rather than by comparing ids here, which cannot resolve
 * parent/variant.
 */

/** Warning ink, matching `nutrition/v2/ui/v2Tokens` `warn`. */
const WARN = "#B4700A";

export type TraineeSwapNoticeProps = {
  /** True when the trainee swapped this exercise out of the trainer's prescription. */
  replacedByTrainee?: boolean;
  /** Hebrew name of the prescribed exercise; may be absent if it left the catalog. */
  originalExerciseNameHeb?: string | null;
  /** English fallback for the prescribed exercise's name. */
  originalExerciseName?: string | null;
  /** `inline` drops the leading margin, for use inside an existing row. */
  variant?: "block" | "inline";
  /** Shrinks the text/icon for the dense summary table. */
  dense?: boolean;
  /**
   * Drop the label and show only the icon, for rows too tight to hold it (a superset header, a
   * summary table cell). The tooltip still carries the full "instead of X", so nothing is lost —
   * and the exercise's own expanded card shows the labelled version.
   */
  iconOnly?: boolean;
};

const TraineeSwapNotice = ({
  replacedByTrainee,
  originalExerciseNameHeb,
  originalExerciseName,
  variant = "block",
  dense = false,
  iconOnly = false,
}: TraineeSwapNoticeProps) => {
  if (!replacedByTrainee) return null;

  const originalName = originalExerciseNameHeb || originalExerciseName || "";
  // Without a name there is nothing to compare against, but the coach still needs to know the
  // workout no longer matches the program.
  const label = originalName ? `הוחלף ע״י המתאמן · במקום ${originalName}` : "הוחלף ע״י המתאמן";

  return (
    <Tooltip
      title={
        originalName
          ? `המתאמן החליף את התרגיל. במקור נקבע: ${originalName}`
          : "המתאמן החליף את התרגיל שנקבע בתוכנית"
      }
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          mt: variant === "inline" ? 0 : 0.35,
          fontSize: dense ? "0.65rem" : "0.7rem",
          fontWeight: 500,
          color: WARN,
          lineHeight: 1.3,
          maxWidth: "100%",
        }}
      >
        <SwapHorizIcon
          sx={{ fontSize: dense ? 13 : 14, flexShrink: 0 }}
          aria-label={iconOnly ? label : undefined}
        />
        {!iconOnly && (
          <Box
            component="span"
            sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {label}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default TraineeSwapNotice;
